import React, { useState } from 'react'

interface QuickAction {
  id: string
  title: string
  description: string
  icon: React.ReactNode
  action: () => void
}

interface QuickActionsProps {
  onActionClick: (actionType: string, data?: any) => void
}

export const QuickActions: React.FC<QuickActionsProps> = ({
  onActionClick,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  const quickActions: QuickAction[] = [
    {
      id: 'check-specialist',
      title: 'Check Specialist Availability',
      description: 'Check available sessions from specific specialist',
      icon: (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      action: () => {
        onActionClick('check-specialist')
        setIsOpen(false)
      },
    },
  ]

  return (
    <div className="relative">
      {/* Hamburger Menu Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          relative group
          bg-gradient-to-r from-blue-50 to-indigo-50 
          hover:from-blue-100 hover:to-indigo-100
          border border-blue-200 hover:border-blue-300
          text-blue-700 hover:text-blue-800
          rounded-xl p-3 
          transition-all duration-200 ease-in-out
          shadow-sm hover:shadow-md
          transform hover:scale-105
        `}
        aria-label="Quick actions menu"
      >
        <svg
          className="w-5 h-5 transition-transform duration-200 group-hover:rotate-180"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2.5}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Quick Actions Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />

          {/* Menu */}
          <div className="absolute bottom-full left-0 mb-3 w-80 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-20 animate-in slide-in-from-bottom-2 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3">
              <h3 className="text-base font-bold text-white flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z"
                  />
                </svg>
                Quick Actions
              </h3>
              <p className="text-xs text-blue-100 mt-1">
                Choose an action to get started
              </p>
            </div>

            {/* Actions List */}
            <div className="p-2">
              {quickActions.map((action, index) => (
                <button
                  key={action.id}
                  onClick={action.action}
                  className={`
                    group w-full p-4 text-left
                    hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50
                    border border-transparent hover:border-blue-200
                    rounded-xl transition-all duration-200 ease-in-out
                    flex items-center space-x-4
                    transform hover:scale-[1.02] hover:shadow-md
                    ${index > 0 ? 'mt-2' : ''}
                  `}
                >
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 group-hover:text-blue-900 transition-colors duration-200">
                      {action.title}
                    </p>
                    <p className="text-xs text-gray-500 group-hover:text-blue-600 mt-1 transition-colors duration-200">
                      {action.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  )
}
